import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { billingApi, type PlanFeatureKey } from '@/api/billing.api';

/**
 * What the clinic's plan includes.
 *
 * The API is the rule (PlanFeatureGuard); this only decides what to show.
 * While the answer is loading, or if it fails, everything reads as included:
 * a false lock on a screen the clinic paid for is worse than a moment of an
 * unlocked screen whose writes the server would refuse anyway.
 */
export function usePlanFeatures() {
  const { clinic } = useAuth();
  const { i18n } = useTranslation();
  const clinicId = clinic?.id ?? '';
  const locale = i18n.language.startsWith('ar') ? 'ar' : 'en';

  const { data, isSuccess } = useQuery({
    queryKey: ['billing', 'features', clinicId, locale],
    queryFn: () => billingApi.features(locale),
    enabled: !!clinicId,
    staleTime: 5 * 60 * 1000,
  });

  const has = useCallback(
    (feature: PlanFeatureKey | undefined) =>
      !feature || !isSuccess || !data || data.features.includes(feature),
    [data, isSuccess],
  );

  const unlockedBy = useCallback(
    (feature: PlanFeatureKey) => data?.unlockedBy?.[feature] ?? null,
    [data],
  );

  return {
    has,
    unlockedBy,
    /** What the plan includes; empty until known. */
    features: data?.features ?? [],
    isKnown: isSuccess,
  };
}
